import { hasLocalizedRoute } from './localized-routes';
import { DEFAULT_LOCALE, isSupportedLocale, type Locale } from './locales';

/**
 * Rewrites an internal href into the active locale.
 *
 * Without this the locale is a one-click dead end: the footer renders
 * `href="/terms"` regardless of locale, so an `/es` visitor clicking
 * "Términos de servicio" lands on the English page and silently loses their
 * language for the rest of the session.
 *
 * ── What gets localised ─────────────────────────────────────────────────────
 *
 * Only paths in LOCALIZED_ROUTE_PATHS. The tempting shortcut — "localise any link
 * that has a messageKey" — is wrong: several translated labels point at
 * English-only hubs (/blog, /podcast, /tools, /alternatives), and prefixing those
 * would send a locale visitor to a 404. The set is explicit and filesystem-checked
 * (see localized-routes.ts).
 */
export function localizeHref(href: string, locale: Locale): string {
  if (locale === DEFAULT_LOCALE) return href;

  // External, anchor, mailto/tel and query-only hrefs are left alone.
  if (!href.startsWith('/') || href.startsWith('//')) return href;

  const [pathPart, ...rest] = href.split(/(?=[?#])/);
  const segments = pathPart.split('/').filter(Boolean);

  // Already localised (or pointing at another locale) — do not double-prefix.
  if (segments.length > 0 && isSupportedLocale(segments[0])) return href;

  const normalised = segments.length ? `/${segments.join('/')}` : '/';

  if (!hasLocalizedRoute(normalised)) return href;

  const localised = `/${locale}${segments.length ? `/${segments.join('/')}` : ''}`;

  return `${localised}${rest.join('')}`;
}
